package fr.frux.core.common.crash

import fr.frux.core.common.logging.Logger
import kotlinx.browser.window

/**
 * Crash reporting navigateur (#482, ADR 0018), commun à webApp et admin-app.
 *
 * Tout vient de `/app-config.js`, comme l'URL de l'API (ADR 0022) :
 * - `window.fruxSentryDsn` : absent ou vide, rien n'est initialisé et aucune requête ne part.
 *   C'est le cas nominal en développement (`dev-web.sh` ne le pose pas).
 * - `window.fruxSentryEnvironment` : `staging` ou `production`, posé par le script de déploiement.
 * - `window.fruxRelease` : sha du commit déployé, préfixé ici par [app].
 *
 * Aucune donnée personnelle : pas d'IP ni d'identité (`sendDefaultPii = false`, utilisateur
 * retiré), aucune query string ni email (cf. [CrashReportScrubber]), pas de session replay, pas
 * de tracing (`tracesSampleRate = 0`, aucune intégration de tracing chargée), pas de session de
 * release health (une requête à chaque ouverture de page, sans intérêt pour le tri des erreurs).
 *
 * Capture les exceptions non interceptées et les rejets de promesse (intégrations par défaut du
 * SDK). Un `Logger.e` n'est qu'un breadcrumb (#554), comme sur Android : Sentry sert aux crashs,
 * une erreur gérée (réseau coupé, timeout) n'y crée aucun événement.
 *
 * @param app nom court de l'application (`frux-web`, `frux-admin`), préfixe de la release et tag.
 */
fun initializeWebCrashReporting(app: String) {
    val config = window.asDynamic()
    val dsn = (config.fruxSentryDsn as? String)?.takeIf { it.isNotBlank() } ?: return
    val environment = (config.fruxSentryEnvironment as? String)?.takeIf { it.isNotBlank() } ?: "development"
    val release = (config.fruxRelease as? String)?.takeIf { it.isNotBlank() }

    val options: dynamic = js("({})")
    options.dsn = dsn
    options.environment = environment
    if (release != null) options.release = "$app@$release"
    options.sendDefaultPii = false
    options.tracesSampleRate = 0
    options.integrations = { defaults: Array<dynamic> ->
        defaults.filter { it.name != "BrowserSession" }.toTypedArray()
    }
    options.beforeBreadcrumb = { crumb: dynamic, _: dynamic -> scrubBreadcrumb(crumb) }
    options.beforeSend = { event: dynamic, _: dynamic -> scrubEvent(event) }
    val tags: dynamic = js("({})")
    tags.app = app
    val initialScope: dynamic = js("({})")
    initialScope.tags = tags
    options.initialScope = initialScope
    init(options)

    Logger.errorSink = { tag, message, throwable ->
        val crumb: dynamic = js("({})")
        crumb.category = CrashReportScrubber.LOG_BREADCRUMB_CATEGORY
        crumb.level = "error"
        crumb.message = CrashReportScrubber.logBreadcrumbMessage(tag, message, throwable)
        addBreadcrumb(crumb)
    }
}

private fun scrubBreadcrumb(crumb: dynamic): dynamic {
    if (!CrashReportScrubber.keepsBreadcrumb(crumb.category as? String)) return null
    val data = crumb.data
    if (data != null) {
        scrubUrlField(data, "url")
        scrubUrlField(data, "from")
        scrubUrlField(data, "to")
    }
    (crumb.message as? String)?.let { crumb.message = CrashReportScrubber.scrubText(it) }
    return crumb
}

private fun scrubEvent(event: dynamic): dynamic {
    event.user = undefined
    val request = event.request
    if (request != null) {
        scrubUrlField(request, "url")
        request.query_string = undefined
        request.cookies = undefined
        // Le Referer d'une page ouverte depuis un lien peut porter une query string (jeton).
        val headers = request.headers
        if (headers != null) {
            headers.Referer = undefined
            headers.referer = undefined
        }
    }
    (event.message as? String)?.let { event.message = CrashReportScrubber.scrubText(it) }
    val values = event.exception?.values as? Array<dynamic>
    values?.forEach { value ->
        (value.value as? String)?.let { value.value = CrashReportScrubber.scrubText(it) }
        // La pile d'une exception Kotlin commence par son message : le parseur du SDK y lit
        // parfois l'URL de la requête (query string comprise) comme nom de fichier d'une frame.
        val frames = value.stacktrace?.frames as? Array<dynamic>
        frames?.forEach { frame ->
            scrubUrlField(frame, "filename")
            scrubUrlField(frame, "abs_path")
        }
    }
    val breadcrumbs = event.breadcrumbs as? Array<dynamic>
    if (breadcrumbs != null) {
        event.breadcrumbs = breadcrumbs.mapNotNull { scrubBreadcrumb(it) }.toTypedArray()
    }
    return event
}

private fun scrubUrlField(
    holder: dynamic,
    key: String,
) {
    (holder[key] as? String)?.let { holder[key] = CrashReportScrubber.scrubUrl(it) }
}
